import React, { useState, useEffect } from "react";
import {
  Box,
  VStack,
  Flex,
  Spinner,
  Text,
  Heading,
  Input,
  InputGroup,
  InputLeftElement,
  FormControl,
  FormLabel,
  Select,
  Textarea,
  Grid,
  GridItem,
  FormErrorMessage,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  Tabs,
  TabList,
  Tab,
  TabPanels,
  TabPanel,
} from "@chakra-ui/react";
import { useNavigate, useLocation } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faFileLines, faSearch, faEdit } from "@fortawesome/free-solid-svg-icons";
import DataTable from "../AppDataTable";
import apiService from "../../services/ApiService";
import { useAuth } from "../../context/AuthContext";
import * as IconButton from "../../themes/IconButtonSet";
import { tableCustomStyles, StatusActive } from "../../themes/TableStyles";
import { StandardHeader } from "../../themes/StandardHeader";

const DLDOfficeManagement = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { sessionId, profile } = useAuth();
  const [offices, setOffices] = useState([]);
  const [filteredOffices, setFilteredOffices] = useState([]);
  const [officeTypes, setOfficeTypes] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterType, setFilterType] = useState("");
  const [filterStatus, setFilterStatus] = useState("");
  const [showForm, setShowForm] = useState(false);
  const [activeTab, setActiveTab] = useState(0);
  const [formData, setFormData] = useState({
    OfficeID: "",
    OfficeType: "",
    OfficeName: "",
    RegionalOffice: "",
    OfficeName2: "",
    AddressNo: "",
    AddressMoo: "",
    AddressRoad: "",
    AddressSubdistrict: "",
    AddressDistrict: "",
    Province: "",
    PostalCode: "",
    Email: "",
    Phone: "",
    Website: "",
    Latitude: "",
    Longitude: "",
    Remark: "",
    Status: "1",
    CMOrderNo: "",
    CMName01: "",
    CMPosition01: "",
    CMName02: "",
    CMPosition02: "",
    CMName03: "",
    CMPosition03: "",
    CMName04: "",
    CMPosition04: "",
    CMName05: "",
    CMPosition05: "",
    CMName06: "",
    CMPosition06: "",
    CMName07: "",
    CMPosition07: "",
  });
  const [isEditing, setIsEditing] = useState(false);
  const [errors, setErrors] = useState({});

  const hasEditPermission = profile?.OfficeID === formData.OfficeID || profile?.OfficeType === "สทช.";

  useEffect(() => {
    if (!sessionId) {
      navigate("/login", { state: { from: location.pathname } });
    }
  }, [sessionId, navigate, location.pathname]);

  const columns = [
    {
      name: "รหัสสำนักงาน",
      selector: (row) => row.OfficeID,
      sortable: true,
      minWidth: "130px",
    },
    {
      name: "ชื่อสำนักงาน",
      selector: (row) => row.OfficeName,
      sortable: true,
      minWidth: "220px",
      grow: 2,
      wrap: true,
    },
    {
      name: "ประเภทสำนักงาน",
      selector: (row) => {
        const type = officeTypes.find((t) => t.OfficeTypeID === row.OfficeType);
        return type ? type.OfficeTypeName : row.OfficeType || "-";
      },
      sortable: true,
      minWidth: "180px",
      grow: 2,
      wrap: true,
      hide: 1024,
    },
    {
      name: "สถานะ",
      selector: (row) => <StatusActive status={row.Status} />,
      sortable: true,
      minWidth: "110px",
    },
    {
      name: "จัดการ",
      cell: (row) => (
        <IconButton.Edit
          icon={faEdit}
          onClick={() => handleEdit(row)}
          label="รายละเอียด"
        />
      ),
      ignoreRowClick: true,
      allowOverflow: true,
      button: true,
      minWidth: "120px",
    },
  ];

  const fetchOfficeTypes = async () => {
    try {
      const response = await apiService({
        method: "POST",
        url: "/getDLDOfficeTypeList",
        data: {},
        sessionId: sessionId || "",
      });
      if (response.result === "success") {
        setOfficeTypes(response.OfficeTypeList || []);
      }
    } catch (error) {
      console.error("Error fetching office types:", error);
    }
  };

  const fetchOffices = async () => {
    setIsLoading(true);
    try {
      const response = await apiService({
        method: "POST",
        url: "/getDLDOfficeWithCommitteeList",
        data: {},
        sessionId: sessionId || "",
      });
      if (response.result === "success") {
        const data = response.OfficeList || [];
        setOffices(data);
        setFilteredOffices(data);
      }
    } catch (error) {
      console.error("Error fetching offices:", error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (sessionId) {
      fetchOffices();
      fetchOfficeTypes();
    }
  }, [sessionId]);

  const handleSearch = (e) => {
    const value = e.target.value.toLowerCase();
    setSearchTerm(value);
    applyFilters(value, filterType, filterStatus);
  };

  const handleTypeFilter = (e) => {
    const value = e.target.value;
    setFilterType(value);
    applyFilters(searchTerm, value, filterStatus);
  };

  const handleStatusFilter = (e) => {
    const value = e.target.value;
    setFilterStatus(value);
    applyFilters(searchTerm, filterType, value);
  };

  const applyFilters = (search, type, status) => {
    let filtered = offices;

    if (search) {
      const searchTerms = search.split(" ").filter((term) => term.length > 0);
      filtered = filtered.filter((row) => {
        const typeName =
          officeTypes.find((t) => t.OfficeTypeID === row.OfficeType)?.OfficeTypeName ||
          row.OfficeType ||
          "";
        return searchTerms.every((term) =>
          [
            row.OfficeID.toLowerCase(),
            row.OfficeName.toLowerCase(),
            typeName.toLowerCase(),
            row.Status ? "active" : "inactive",
          ].some((field) => field.includes(term))
        );
      });
    }

    if (type) {
      filtered = filtered.filter((row) => row.OfficeType === type);
    }

    if (status) {
      filtered = filtered.filter((row) =>
        status === "1" ? row.Status : !row.Status
      );
    }

    setFilteredOffices(filtered);
  };

  const handleClearSearch = () => {
    setSearchTerm("");
    setFilterType("");
    setFilterStatus("");
    setFilteredOffices(offices);
  };

  const handleAddNew = () => {
    setFormData({
      OfficeID: "",
      OfficeType: "",
      OfficeName: "",
      RegionalOffice: "",
      OfficeName2: "",
      AddressNo: "",
      AddressMoo: "",
      AddressRoad: "",
      AddressSubdistrict: "",
      AddressDistrict: "",
      Province: "",
      PostalCode: "",
      Email: "",
      Phone: "",
      Website: "",
      Latitude: "",
      Longitude: "",
      Remark: "",
      Status: "1",
      CMOrderNo: "",
      CMName01: "",
      CMPosition01: "",
      CMName02: "",
      CMPosition02: "",
      CMName03: "",
      CMPosition03: "",
      CMName04: "",
      CMPosition04: "",
      CMName05: "",
      CMPosition05: "",
      CMName06: "",
      CMPosition06: "",
      CMName07: "",
      CMPosition07: "",
    });
    setIsEditing(false);
    setShowForm(true);
    setActiveTab(0);
    setErrors({});
  };

  const handleEdit = (row) => {
    setFormData({
      ...row,
      Status: row.Status ? "1" : "0",
      Latitude: row.Latitude || "",
      Longitude: row.Longitude || "",
      CMOrderNo: row.CMOrderNo || "",
      CMName01: row.CMName01 || "",
      CMPosition01: row.CMPosition01 || "",
      CMName02: row.CMName02 || "",
      CMPosition02: row.CMPosition02 || "",
      CMName03: row.CMName03 || "",
      CMPosition03: row.CMPosition03 || "",
      CMName04: row.CMName04 || "",
      CMPosition04: row.CMPosition04 || "",
      CMName05: row.CMName05 || "",
      CMPosition05: row.CMPosition05 || "",
      CMName06: row.CMName06 || "",
      CMPosition06: row.CMPosition06 || "",
      CMName07: row.CMName07 || "",
      CMPosition07: row.CMPosition07 || "",
    });
    setIsEditing(true);
    setShowForm(true);
    setActiveTab(0);
    setErrors({});
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
    validateField(name, value);
  };

  const validateField = (name, value) => {
    let newErrors = { ...errors };
    switch (name) {
      case "OfficeID":
      case "OfficeName":
      case "OfficeType":
        if (!value) {
          newErrors[name] = "กรุณากรอกข้อมูล";
        } else {
          delete newErrors[name];
        }
        break;
      case "Email":
        if (value && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
          newErrors[name] = "กรุณากรอกอีเมลที่ถูกต้อง";
        } else {
          delete newErrors[name];
        }
        break;
      case "PostalCode":
        if (value && !/^\d{5}$/.test(value)) {
          newErrors[name] = "รหัสไปรษณีย์ต้องเป็นตัวเลข 5 หลัก";
        } else {
          delete newErrors[name];
        }
        break;
      case "Latitude":
      case "Longitude":
        if (value && isNaN(value)) {
          newErrors[name] = "ต้องเป็นตัวเลข";
        } else {
          delete newErrors[name];
        }
        break;
      default:
        break;
    }
    setErrors(newErrors);
  };

  const isFormValid = () => {
    return (
      formData.OfficeID &&
      formData.OfficeName &&
      formData.OfficeType &&
      Object.keys(errors).length === 0
    );
  };

  const handleSave = async () => {
    if (!hasEditPermission) {
      alert("คุณไม่มีสิทธิ์ในการบันทึกข้อมูล");
      return;
    }
    if (!isFormValid()) {
      // ข้อมูลที่บังคับกรอกอยู่ในแท็บแรกทั้งหมด จึงพากลับไปแท็บนั้นให้เห็นช่องที่ยังไม่ผ่าน
      setActiveTab(0);
      alert("กรุณากรอกข้อมูลที่จำเป็นให้ครบถ้วน");
      return;
    }
    setIsLoading(true);
    try {
      const apiUrl = isEditing
        ? "/updateDLDOfficeByID"
        : "/createDLDOffice";
      const response = await apiService({
        method: "POST",
        url: apiUrl,
        data: {
          ...formData,
          Status: formData.Status === "1",
          Latitude: formData.Latitude ? parseFloat(formData.Latitude) : null,
          Longitude: formData.Longitude ? parseFloat(formData.Longitude) : null,
        },
        sessionId: sessionId || "",
      });
      if (response.result === "success") {
        alert(isEditing ? "อัปเดตสำนักงานสำเร็จ!" : "สร้างสำนักงานสำเร็จ!");
        setShowForm(false);
        fetchOffices();
      }
    } catch (error) {
      console.error("Error saving office:", error);
      alert("เกิดข้อผิดพลาดในการบันทึก: " + error.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancel = () => {
    setShowForm(false);
    setFormData({
      OfficeID: "",
      OfficeType: "",
      OfficeName: "",
      RegionalOffice: "",
      OfficeName2: "",
      AddressNo: "",
      AddressMoo: "",
      AddressRoad: "",
      AddressSubdistrict: "",
      AddressDistrict: "",
      Province: "",
      PostalCode: "",
      Email: "",
      Phone: "",
      Website: "",
      Latitude: "",
      Longitude: "",
      Remark: "",
      Status: "1",
      CMOrderNo: "",
      CMName01: "",
      CMPosition01: "",
      CMName02: "",
      CMPosition02: "",
      CMName03: "",
      CMPosition03: "",
      CMName04: "",
      CMPosition04: "",
      CMName05: "",
      CMPosition05: "",
      CMName06: "",
      CMPosition06: "",
      CMName07: "",
      CMPosition07: "",
    });
    setErrors({});
  };

  // ไม่ลบข้อมูลออกจากฐานข้อมูล เพราะรหัสสำนักงานถูกอ้างอิงไว้ในข้อมูลเจ้าหน้าที่และเอกสารเดิม
  const handleToggleStatus = async () => {
    if (!hasEditPermission) {
      alert("คุณไม่มีสิทธิ์ในการปรับสถานะข้อมูล");
      return;
    }
    const nextStatus = formData.Status !== "1";
    const confirmMessage = nextStatus
      ? "ยืนยันการเปิดการใช้งานสำนักงานนี้ จะกลับมาเลือกใช้ในรายการใหม่ได้อีกครั้ง"
      : "ยืนยันการปิดการใช้งานสำนักงานนี้ จะไม่ปรากฏให้เลือกในรายการใหม่ แต่ข้อมูลและเอกสารเดิมที่อ้างอิงไว้จะยังอยู่ในระบบ";

    if (!window.confirm(confirmMessage)) return;

    setIsLoading(true);
    try {
      const response = await apiService({
        method: "POST",
        url: "/updateDLDOfficeByID",
        data: {
          OfficeID: formData.OfficeID,
          Status: nextStatus,
          UpdatedBy: profile?.DLDEmployeeID || profile?.UserID || "",
        },
        sessionId: sessionId || "",
      });
      if (response.result === "success") {
        alert(
          nextStatus
            ? "เปิดการใช้งานสำนักงานสำเร็จ"
            : "ปิดการใช้งานสำนักงานสำเร็จ"
        );
        setShowForm(false);
        fetchOffices();
      }
    } catch (error) {
      console.error("Error updating office status:", error);
      alert("เกิดข้อผิดพลาดในการปรับสถานะ: " + error.message);
    } finally {
      setIsLoading(false);
    }
  };

  if (!sessionId) {
    return null;
  }

  return (
    <Box
      p={{ base: 3, sm: 4, md: 8, lg: 12 }}
      m={{ base: 2, md: 6, lg: 10 }}
      borderWidth={1}
      borderRadius="lg"
      boxShadow="lg"
    >
      <VStack spacing={{ base: 4, md: 6 }} align="stretch">
        <StandardHeader
          title="รายการข้อมูลสำนักงาน"
          bgColor="iBlue.600"
          icon={faFileLines}
        />

        {/* ส่วนตัวกรอง */}
        <Flex
          w="100%"
          gap={3}
          direction={{ base: "column", lg: "row" }}
          align={{ base: "stretch", lg: "center" }}
          justify="space-between"
        >
          <Flex gap={3} wrap="wrap" align="center" flex="1" minW={0}>
            <InputGroup w={{ base: "100%", sm: "260px" }}>
              <InputLeftElement pointerEvents="none">
                <FontAwesomeIcon icon={faSearch} color="gray.300" />
              </InputLeftElement>
              <Input
                placeholder="ค้นหาสำนักงาน..."
                value={searchTerm}
                onChange={handleSearch}
                colorScheme="iBlue"
              />
            </InputGroup>
            <Select
              w={{ base: "100%", sm: "200px" }}
              value={filterType}
              onChange={handleTypeFilter}
              placeholder="เลือกประเภทสำนักงาน"
              colorScheme="iBlue"
            >
              <option value="">ทั้งหมด</option>
              {officeTypes.map((type) => (
                <option key={type.OfficeTypeID} value={type.OfficeTypeID}>
                  {type.OfficeTypeName}
                </option>
              ))}
            </Select>
            <Select
              w={{ base: "100%", sm: "180px" }}
              value={filterStatus}
              onChange={handleStatusFilter}
              placeholder="เลือกสถานะ"
              colorScheme="iBlue"
            >
              <option value="">ทั้งหมด</option>
              <option value="1">Active</option>
              <option value="0">Inactive</option>
            </Select>
            <IconButton.Clear
              onClick={handleClearSearch}
              label="ล้าง"
              w={{ base: "100%", sm: "auto" }}
            />
          </Flex>
          {hasEditPermission && (
            <IconButton.Add
              onClick={handleAddNew}
              label="เพิ่มข้อมูลใหม่"
              w={{ base: "100%", lg: "auto" }}
            />
          )}
        </Flex>

        {isLoading ? (
          <Flex justify="center" py={8}>
            <Spinner size="lg" />
          </Flex>
        ) : offices.length === 0 ? (
          <Text
            fontSize={{ base: "md", md: "lg" }}
            color="gray.500"
            textAlign="center"
            w="100%"
            py={8}
          >
            ยังไม่มีข้อมูลสำนักงาน
          </Text>
        ) : filteredOffices.length === 0 ? (
          <Text
            fontSize={{ base: "md", md: "lg" }}
            color="gray.500"
            textAlign="center"
            w="100%"
            py={8}
          >
            ไม่พบข้อมูลที่ค้นหา
          </Text>
        ) : (
          <Box w="100%" overflowX="auto">
            <DataTable
              columns={columns}
              data={filteredOffices}
              pagination
              highlightOnHover
              striped
              responsive
              customStyles={tableCustomStyles}
            />
          </Box>
        )}
      </VStack>

      {/* Modal สำหรับเพิ่ม/แก้ไขข้อมูลสำนักงาน */}
      <Modal
        isOpen={showForm}
        onClose={handleCancel}
        size={{ base: "full", md: "3xl", lg: "5xl", xl: "6xl" }}
        scrollBehavior="inside"
        isCentered
      >
        <ModalOverlay />
        <ModalContent maxH={{ base: "100vh", md: "90vh" }} my={{ base: 0, md: 4 }}>
          <ModalHeader
            bg="iBlue.600"
            color="white"
            borderTopRadius={{ base: 0, md: "md" }}
            py={{ base: 3, md: 4 }}
          >
            <Heading size={{ base: "sm", md: "md" }} fontWeight="normal" pr={8}>
              {hasEditPermission
                ? isEditing
                  ? "แก้ไขข้อมูลสำนักงาน"
                  : "เพิ่มข้อมูลสำนักงาน"
                : "รายละเอียดสำนักงาน"}
            </Heading>
          </ModalHeader>
          <ModalCloseButton color="white" />
          <ModalBody py={{ base: 4, md: 6 }} px={{ base: 3, md: 6 }}>
            <Tabs
              index={activeTab}
              onChange={setActiveTab}
              colorScheme="iBlue"
              variant="enclosed"
            >
              <TabList flexWrap="wrap">
                <Tab
                  fontSize={{ base: "sm", md: "md" }}
                  whiteSpace="normal"
                  textAlign="left"
                >
                  ข้อมูลทั่วไป
                </Tab>
                <Tab
                  fontSize={{ base: "sm", md: "md" }}
                  whiteSpace="normal"
                  textAlign="left"
                >
                  ข้อมูลการแต่งตั้งคณะกรรมการ ตรวจนับวัคซีน (ตามผช. 37)
                </Tab>
              </TabList>
              <TabPanels>
                <TabPanel px={0} pt={{ base: 4, md: 5 }} pb={0}>
                  <Grid
                    templateColumns={{
                      base: "1fr",
                      sm: "repeat(2, 1fr)",
                      lg: "repeat(3, 1fr)",
                    }}
                    gap={{ base: 4, md: 6 }}
                  >
                    <GridItem colSpan={1}>
                      <FormControl
                        isRequired
                        isInvalid={!!errors.OfficeID}
                        isReadOnly={!hasEditPermission}
                        isDisabled={isEditing && hasEditPermission}
                      >
                        <FormLabel fontSize={{ base: "sm", md: "md" }}>รหัสสำนักงาน</FormLabel>
                        <Input
                          name="OfficeID"
                          value={formData.OfficeID}
                          onChange={handleInputChange}
                          colorScheme="iBlue"
                        />
                        <FormErrorMessage>{errors.OfficeID}</FormErrorMessage>
                      </FormControl>
                    </GridItem>
                    <GridItem colSpan={{ base: 1, sm: 2, lg: 2 }}>
                      <FormControl
                        isRequired
                        isInvalid={!!errors.OfficeName}
                        isReadOnly={!hasEditPermission}
                      >
                        <FormLabel fontSize={{ base: "sm", md: "md" }}>ชื่อสำนักงาน</FormLabel>
                        <Input
                          name="OfficeName"
                          value={formData.OfficeName}
                          onChange={handleInputChange}
                          colorScheme="iBlue"
                        />
                        <FormErrorMessage>{errors.OfficeName}</FormErrorMessage>
                      </FormControl>
                    </GridItem>
                    <GridItem colSpan={1}>
                      <FormControl
                        isRequired
                        isInvalid={!!errors.OfficeType}
                        isDisabled={!hasEditPermission}
                      >
                        <FormLabel fontSize={{ base: "sm", md: "md" }}>ประเภทสำนักงาน</FormLabel>
                        <Select
                          name="OfficeType"
                          value={formData.OfficeType}
                          onChange={handleInputChange}
                          placeholder="เลือกประเภท"
                          colorScheme="iBlue"
                        >
                          {officeTypes.map((type) => (
                            <option key={type.OfficeTypeID} value={type.OfficeTypeID}>
                              {type.OfficeTypeName}
                            </option>
                          ))}
                        </Select>
                        <FormErrorMessage>{errors.OfficeType}</FormErrorMessage>
                      </FormControl>
                    </GridItem>
                    <GridItem colSpan={{ base: 1, sm: 2, lg: 2 }}>
                      <FormControl isReadOnly={!hasEditPermission}>
                        <FormLabel fontSize={{ base: "sm", md: "md" }}>ชื่อสำนักงาน (ภาษาอังกฤษ)</FormLabel>
                        <Input
                          name="OfficeName2"
                          value={formData.OfficeName2}
                          onChange={handleInputChange}
                          colorScheme="iBlue"
                        />
                      </FormControl>
                    </GridItem>
                    <GridItem colSpan={{ base: 1, sm: 2, lg: 3 }}>
                      <FormControl isReadOnly={!hasEditPermission}>
                        <FormLabel fontSize={{ base: "sm", md: "md" }}>สังกัดสำนักงานปศุสัตว์เขต</FormLabel>
                        <Input
                          name="RegionalOffice"
                          value={formData.RegionalOffice}
                          onChange={handleInputChange}
                          colorScheme="iBlue"
                        />
                      </FormControl>
                    </GridItem>
                    <GridItem colSpan={{ base: 1, sm: 2, lg: 2 }}>
                      <FormControl isReadOnly={!hasEditPermission}>
                        <FormLabel fontSize={{ base: "sm", md: "md" }}>เลขที่อยู่ / อาคาร / ชั้น</FormLabel>
                        <Input
                          name="AddressNo"
                          value={formData.AddressNo}
                          onChange={handleInputChange}
                          colorScheme="iBlue"
                        />
                      </FormControl>
                    </GridItem>
                    <GridItem colSpan={1}>
                      <FormControl isReadOnly={!hasEditPermission}>
                        <FormLabel fontSize={{ base: "sm", md: "md" }}>หมู่ที่</FormLabel>
                        <Input
                          name="AddressMoo"
                          value={formData.AddressMoo}
                          onChange={handleInputChange}
                          colorScheme="iBlue"
                        />
                      </FormControl>
                    </GridItem>
                    <GridItem colSpan={1}>
                      <FormControl isReadOnly={!hasEditPermission}>
                        <FormLabel fontSize={{ base: "sm", md: "md" }}>ถนน</FormLabel>
                        <Input
                          name="AddressRoad"
                          value={formData.AddressRoad}
                          onChange={handleInputChange}
                          colorScheme="iBlue"
                        />
                      </FormControl>
                    </GridItem>
                    <GridItem colSpan={1}>
                      <FormControl isReadOnly={!hasEditPermission}>
                        <FormLabel fontSize={{ base: "sm", md: "md" }}>ตำบล</FormLabel>
                        <Input
                          name="AddressSubdistrict"
                          value={formData.AddressSubdistrict}
                          onChange={handleInputChange}
                          colorScheme="iBlue"
                        />
                      </FormControl>
                    </GridItem>
                    <GridItem colSpan={1}>
                      <FormControl isReadOnly={!hasEditPermission}>
                        <FormLabel fontSize={{ base: "sm", md: "md" }}>อำเภอ</FormLabel>
                        <Input
                          name="AddressDistrict"
                          value={formData.AddressDistrict}
                          onChange={handleInputChange}
                          colorScheme="iBlue"
                        />
                      </FormControl>
                    </GridItem>
                    <GridItem colSpan={1}>
                      <FormControl isReadOnly={!hasEditPermission}>
                        <FormLabel fontSize={{ base: "sm", md: "md" }}>จังหวัด</FormLabel>
                        <Input
                          name="Province"
                          value={formData.Province}
                          onChange={handleInputChange}
                          colorScheme="iBlue"
                        />
                      </FormControl>
                    </GridItem>
                    <GridItem colSpan={1}>
                      <FormControl
                        isInvalid={!!errors.PostalCode}
                        isReadOnly={!hasEditPermission}
                      >
                        <FormLabel fontSize={{ base: "sm", md: "md" }}>รหัสไปรษณีย์</FormLabel>
                        <Input
                          name="PostalCode"
                          value={formData.PostalCode}
                          onChange={handleInputChange}
                          colorScheme="iBlue"
                        />
                        <FormErrorMessage>{errors.PostalCode}</FormErrorMessage>
                      </FormControl>
                    </GridItem>
                    <GridItem colSpan={1}>
                      <FormControl
                        isInvalid={!!errors.Email}
                        isReadOnly={!hasEditPermission}
                      >
                        <FormLabel fontSize={{ base: "sm", md: "md" }}>อีเมล</FormLabel>
                        <Input
                          name="Email"
                          value={formData.Email}
                          onChange={handleInputChange}
                          colorScheme="iBlue"
                        />
                        <FormErrorMessage>{errors.Email}</FormErrorMessage>
                      </FormControl>
                    </GridItem>
                    <GridItem colSpan={1}>
                      <FormControl isReadOnly={!hasEditPermission}>
                        <FormLabel fontSize={{ base: "sm", md: "md" }}>โทรศัพท์</FormLabel>
                        <Input
                          name="Phone"
                          value={formData.Phone}
                          onChange={handleInputChange}
                          colorScheme="iBlue"
                        />
                      </FormControl>
                    </GridItem>
                    <GridItem colSpan={{ base: 1, sm: 2, lg: 2 }}>
                      <FormControl isReadOnly={!hasEditPermission}>
                        <FormLabel fontSize={{ base: "sm", md: "md" }}>Website</FormLabel>
                        <Input
                          name="Website"
                          value={formData.Website}
                          onChange={handleInputChange}
                          colorScheme="iBlue"
                        />
                      </FormControl>
                    </GridItem>
                    <GridItem colSpan={1}>
                      <FormControl
                        isInvalid={!!errors.Latitude}
                        isReadOnly={!hasEditPermission}
                      >
                        <FormLabel fontSize={{ base: "sm", md: "md" }}>Latitude</FormLabel>
                        <Input
                          name="Latitude"
                          value={formData.Latitude}
                          onChange={handleInputChange}
                          colorScheme="iBlue"
                        />
                        <FormErrorMessage>{errors.Latitude}</FormErrorMessage>
                      </FormControl>
                    </GridItem>
                    <GridItem colSpan={1}>
                      <FormControl
                        isInvalid={!!errors.Longitude}
                        isReadOnly={!hasEditPermission}
                      >
                        <FormLabel fontSize={{ base: "sm", md: "md" }}>Longitude</FormLabel>
                        <Input
                          name="Longitude"
                          value={formData.Longitude}
                          onChange={handleInputChange}
                          colorScheme="iBlue"
                        />
                        <FormErrorMessage>{errors.Longitude}</FormErrorMessage>
                      </FormControl>
                    </GridItem>
                    <GridItem colSpan={{ base: 1, sm: 2, lg: 3 }}>
                      <FormControl isReadOnly={!hasEditPermission}>
                        <FormLabel fontSize={{ base: "sm", md: "md" }}>หมายเหตุ</FormLabel>
                        <Textarea
                          name="Remark"
                          value={formData.Remark}
                          onChange={handleInputChange}
                          colorScheme="iBlue"
                        />
                      </FormControl>
                    </GridItem>
                    <GridItem colSpan={1}>
                      <FormControl>
                        <FormLabel fontSize={{ base: "sm", md: "md" }}>สถานะการใช้งาน</FormLabel>
                        <Flex align="center" gap={3} minH="40px">
                          <StatusActive status={formData.Status === "1"} />
                          <Text fontSize={{ base: "xs", md: "sm" }} color="gray.500">
                            {isEditing
                              ? "ปรับได้จากปุ่มปิด/เปิดการใช้งานด้านล่าง"
                              : "ข้อมูลที่สร้างใหม่จะเปิดการใช้งานทันที"}
                          </Text>
                        </Flex>
                      </FormControl>
                    </GridItem>
                  </Grid>
                </TabPanel>
                <TabPanel px={0} pt={{ base: 4, md: 5 }} pb={0}>
                  <Grid
                    templateColumns={{
                      base: "1fr",
                      sm: "repeat(2, 1fr)",
                      lg: "repeat(3, 1fr)",
                    }}
                    gap={{ base: 4, md: 6 }}
                  >
                    <GridItem colSpan={{ base: 1, sm: 2, lg: 3 }}>
                      <FormControl isReadOnly={!hasEditPermission}>
                        <FormLabel fontSize={{ base: "sm", md: "md" }}>เลขที่คำสั่งแต่งตั้ง</FormLabel>
                        <Input
                          name="CMOrderNo"
                          value={formData.CMOrderNo}
                          onChange={handleInputChange}
                          colorScheme="iBlue"
                        />
                      </FormControl>
                    </GridItem>
                    <GridItem colSpan={1}>
                      <FormControl isReadOnly={!hasEditPermission}>
                        <FormLabel fontSize={{ base: "sm", md: "md" }}>ประธานกรรมการ</FormLabel>
                        <Input
                          name="CMName01"
                          value={formData.CMName01}
                          onChange={handleInputChange}
                          colorScheme="iBlue"
                        />
                      </FormControl>
                    </GridItem>
                    <GridItem colSpan={{ base: 1, sm: 1, lg: 2 }}>
                      <FormControl isReadOnly={!hasEditPermission}>
                        <FormLabel fontSize={{ base: "sm", md: "md" }}>ตำแหน่งประธานกรรมการ</FormLabel>
                        <Input
                          name="CMPosition01"
                          value={formData.CMPosition01}
                          onChange={handleInputChange}
                          colorScheme="iBlue"
                        />
                      </FormControl>
                    </GridItem>
                    <GridItem colSpan={1}>
                      <FormControl isReadOnly={!hasEditPermission}>
                        <FormLabel fontSize={{ base: "sm", md: "md" }}>กรรมการ #1</FormLabel>
                        <Input
                          name="CMName02"
                          value={formData.CMName02}
                          onChange={handleInputChange}
                          colorScheme="iBlue"
                        />
                      </FormControl>
                    </GridItem>
                    <GridItem colSpan={{ base: 1, sm: 1, lg: 2 }}>
                      <FormControl isReadOnly={!hasEditPermission}>
                        <FormLabel fontSize={{ base: "sm", md: "md" }}>ตำแหน่งกรรมการ #1</FormLabel>
                        <Input
                          name="CMPosition02"
                          value={formData.CMPosition02}
                          onChange={handleInputChange}
                          colorScheme="iBlue"
                        />
                      </FormControl>
                    </GridItem>
                    <GridItem colSpan={1}>
                      <FormControl isReadOnly={!hasEditPermission}>
                        <FormLabel fontSize={{ base: "sm", md: "md" }}>กรรมการ #2</FormLabel>
                        <Input
                          name="CMName03"
                          value={formData.CMName03}
                          onChange={handleInputChange}
                          colorScheme="iBlue"
                        />
                      </FormControl>
                    </GridItem>
                    <GridItem colSpan={{ base: 1, sm: 1, lg: 2 }}>
                      <FormControl isReadOnly={!hasEditPermission}>
                        <FormLabel fontSize={{ base: "sm", md: "md" }}>ตำแหน่งกรรมการ #2</FormLabel>
                        <Input
                          name="CMPosition03"
                          value={formData.CMPosition03}
                          onChange={handleInputChange}
                          colorScheme="iBlue"
                        />
                      </FormControl>
                    </GridItem>
                    <GridItem colSpan={1}>
                      <FormControl isReadOnly={!hasEditPermission}>
                        <FormLabel fontSize={{ base: "sm", md: "md" }}>กรรมการ #3</FormLabel>
                        <Input
                          name="CMName04"
                          value={formData.CMName04}
                          onChange={handleInputChange}
                          colorScheme="iBlue"
                        />
                      </FormControl>
                    </GridItem>
                    <GridItem colSpan={{ base: 1, sm: 1, lg: 2 }}>
                      <FormControl isReadOnly={!hasEditPermission}>
                        <FormLabel fontSize={{ base: "sm", md: "md" }}>ตำแหน่งกรรมการ #3</FormLabel>
                        <Input
                          name="CMPosition04"
                          value={formData.CMPosition04}
                          onChange={handleInputChange}
                          colorScheme="iBlue"
                        />
                      </FormControl>
                    </GridItem>
                    <GridItem colSpan={1}>
                      <FormControl isReadOnly={!hasEditPermission}>
                        <FormLabel fontSize={{ base: "sm", md: "md" }}>ผู้อนุมัติ</FormLabel>
                        <Input
                          name="CMName05"
                          value={formData.CMName05}
                          onChange={handleInputChange}
                          colorScheme="iBlue"
                        />
                      </FormControl>
                    </GridItem>
                    <GridItem colSpan={{ base: 1, sm: 1, lg: 2 }}>
                      <FormControl isReadOnly={!hasEditPermission}>
                        <FormLabel fontSize={{ base: "sm", md: "md" }}>ตำแหน่งผู้อนุมัติ</FormLabel>
                        <Input
                          name="CMPosition05"
                          value={formData.CMPosition05}
                          onChange={handleInputChange}
                          colorScheme="iBlue"
                        />
                      </FormControl>
                    </GridItem>
                    <GridItem colSpan={1}>
                      <FormControl isReadOnly={!hasEditPermission}>
                        <FormLabel fontSize={{ base: "sm", md: "md" }}>ผู้จัดทำรายงาน</FormLabel>
                        <Input
                          name="CMName06"
                          value={formData.CMName06}
                          onChange={handleInputChange}
                          colorScheme="iBlue"
                        />
                      </FormControl>
                    </GridItem>
                    <GridItem colSpan={{ base: 1, sm: 1, lg: 2 }}>
                      <FormControl isReadOnly={!hasEditPermission}>
                        <FormLabel fontSize={{ base: "sm", md: "md" }}>ตำแหน่งผู้จัดทำรายงาน</FormLabel>
                        <Input
                          name="CMPosition06"
                          value={formData.CMPosition06}
                          onChange={handleInputChange}
                          colorScheme="iBlue"
                        />
                      </FormControl>
                    </GridItem>
                    <GridItem colSpan={1}>
                      <FormControl isReadOnly={!hasEditPermission}>
                        <FormLabel fontSize={{ base: "sm", md: "md" }}>ผู้ตรวจสอบรายงาน</FormLabel>
                        <Input
                          name="CMName07"
                          value={formData.CMName07}
                          onChange={handleInputChange}
                          colorScheme="iBlue"
                        />
                      </FormControl>
                    </GridItem>
                    <GridItem colSpan={{ base: 1, sm: 1, lg: 2 }}>
                      <FormControl isReadOnly={!hasEditPermission}>
                        <FormLabel fontSize={{ base: "sm", md: "md" }}>ตำแหน่งผู้ตรวจสอบรายงาน</FormLabel>
                        <Input
                          name="CMPosition07"
                          value={formData.CMPosition07}
                          onChange={handleInputChange}
                          colorScheme="iBlue"
                        />
                      </FormControl>
                    </GridItem>
                  </Grid>
                </TabPanel>
              </TabPanels>
            </Tabs>
          </ModalBody>
          <ModalFooter gap={2} flexWrap="wrap" justifyContent="flex-end">
            <IconButton.Close
              onClick={handleCancel}
              label="ปิด"
              flex={{ base: "1 1 100%", sm: "0 0 auto" }}
            />
            {hasEditPermission &&
              isEditing &&
              (formData.Status === "1" ? (
                <IconButton.Cancel
                  onClick={handleToggleStatus}
                  label="ปิดการใช้งาน"
                  flex={{ base: "1 1 100%", sm: "0 0 auto" }}
                />
              ) : (
                <IconButton.Approved
                  onClick={handleToggleStatus}
                  label="เปิดการใช้งาน"
                  flex={{ base: "1 1 100%", sm: "0 0 auto" }}
                />
              ))}
            {hasEditPermission && (
              <IconButton.Save
                onClick={handleSave}
                isLoading={isLoading}
                flex={{ base: "1 1 100%", sm: "0 0 auto" }}
              />
            )}
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
};

export default DLDOfficeManagement;